import type { Scope, TeamSummary } from "@69ly/shared";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

const STORAGE_KEY = "69ly.currentTeam";
export const PERSONAL = "personal";

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Private windows can refuse storage: the choice then lasts for this visit only.
  }
}

interface CurrentTeam {
  /** The team being looked at; undefined in the personal library. */
  team: TeamSummary | undefined;
  /** Which library the pages read and write: a team ID or "personal". */
  scope: Scope;
  /** A team ID, or "personal". */
  select(id: string): void;
}

const Ctx = createContext<CurrentTeam>({ team: undefined, scope: PERSONAL, select: () => undefined });

/** The library the player is looking at, remembered between visits on this browser. */
export function CurrentTeamProvider({ teams, children }: { teams: TeamSummary[]; children: ReactNode }) {
  const [chosen, setChosen] = useState<string | null>(readStored);
  const select = useCallback((id: string) => {
    setChosen(id);
    writeStored(id);
  }, []);
  const team = useMemo(
    () => (chosen === PERSONAL ? undefined : (teams.find((t) => t.id === chosen) ?? teams[0])),
    [teams, chosen],
  );
  const value = useMemo(() => ({ team, scope: (team?.id ?? PERSONAL) as Scope, select }), [team, select]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useCurrentTeam = () => useContext(Ctx);
